import { useRef, useState, useEffect } from "react";
import { createBlobUrl, cleanupBlobUrls } from "./sdkAssets";

interface IdScanJourneyProps {
  backendUrl: string;
  token: string;
  journeyDefinitionId: string;
  defaultInputProvider?: string;
  tripleScanAnalyzerGuidancePage?: boolean;
  fileUploadOnCameraCaptureJourneys?: boolean;
  previewPages?: any;
  manualCapture?: boolean;
  assetsDirectory?: string;
  smartCapture?: any;
  isVerboseLogEnabled?: boolean;
  hideLogo?: boolean;
  hideAutoCaptureHints?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const SCRIPTS = [
  "vendor.axios.cee2297d8064255b0106.js",
  "vendor.browserify-sign.262d500dac7fbd793b85.js",
  "vendor.elliptic.8350666830498dccd184.js",
  "vendor.browserify-aes.bc1671aedb76de95cac6.js",
  "vendor.stream-browserify.7ad9b49390911acdde5b.js",
  "vendor.asn1.js.3483cc7f63182a3f0b82.js",
  "vendor.hash.js.9a08305c13c240a0f2eb.js",
  "vendor.public-encrypt.deb04f7e80668dc1211e.js",
  "vendor.diffie-hellman.96fa799e490efa568667.js",
  "vendor.signalr-no-jquery.6ee4fa921ba8dc0eba79.js",
  "vendor.miller-rabin.dd407c301bc8de89c1bd.js",
  "vendor.create-ecdh.69ce73dde28c4e953a9b.js",
  "vendor.sjcl.f4547262815d41f5fc42.js",
  "vendor.mustache.0f5d8a4748abe782331b.js",
  "vendor.mobile-device-detect.8de5473c1f1106923305.js",
  "vendor.lottie-web.c63ca72f96d1c4837c16.js",
  "vendor.buffer.f36bb1254e482f86b62a.js",
  "vendor.bn.js.8949dfa435f84f8e13a5.js",
  "idscan-jcs-43dd7041.2b3ac18c07ac038ba585.js",
  "idscan-jcs-d91a9049.194314e02268c234efac.js",
  "idscan-jcs-77455583.178cfd426c47545c4f6f.js",
  "idscan-jcs-3bb2ab8b.05fb7793762bf9f39d33.js",
  "idscan-jcs-1ab2fc53.d7c35cd3b5fff8a83ca1.js",
  "idscan-jcs-10de8631.f74a686e9a7f6e42670c.js",
  "idscan-jcs-76945664.16afcfbbc3f1014ec443.js",
  "idscan-jcs-89419957.ffffb2cf10c5a8b1202d.js",
  "idscan-jcs-6b882012.93839b50b8a7df33fef9.js",
];

const STYLESHEET = "styles.css";

export const LocalIdScanJourney: React.FC<IdScanJourneyProps> = ({
  backendUrl,
  token,
  journeyDefinitionId,
  defaultInputProvider = "SMART_CAPTURE",
  tripleScanAnalyzerGuidancePage = true,
  fileUploadOnCameraCaptureJourneys = false,
  previewPages,
  manualCapture,
  assetsDirectory = "assets",
  smartCapture,
  isVerboseLogEnabled = false,
  hideLogo = false,
  hideAutoCaptureHints = false,
  className,
  style,
  ...rest
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [scriptsLoaded, setScriptsLoaded] = useState(false);

  useEffect(() => {
    const loadScripts = async () => {
      try {
        // Load scripts from bundled assets using blob URLs
        for (const scriptName of SCRIPTS) {
          await new Promise<void>((resolve, reject) => {
            // Check if script is already loaded
            const existingScript = document.querySelector(
              `script[data-gbg-script="${scriptName}"]`
            );
            if (existingScript) {
              resolve();
              return;
            }

            try {
              const blobUrl = createBlobUrl(scriptName);
              const script = document.createElement("script");
              script.src = blobUrl;
              script.defer = true;
              script.setAttribute("data-gbg-script", scriptName);
              script.onload = () => resolve();
              script.onerror = () =>
                reject(
                  new Error(`Failed to load bundled script: ${scriptName}`)
                );
              document.head.appendChild(script);
            } catch (err) {
              reject(err);
            }
          });
        }

        // Load stylesheet from bundled assets
        const existingLink = document.querySelector(
          `link[data-gbg-style="${STYLESHEET}"]`
        );
        if (!existingLink) {
          const blobUrl = createBlobUrl(STYLESHEET);
          const link = document.createElement("link");
          link.rel = "stylesheet";
          link.href = blobUrl;
          link.setAttribute("data-gbg-style", STYLESHEET);
          document.head.appendChild(link);
        }

        // ...existing custom styles code...
        const existingCustomStyles = document.querySelector(
          "style[data-jcs-custom-styles]"
        );
        if (!existingCustomStyles) {
          const customStyles = document.createElement("style");
          customStyles.setAttribute("data-jcs-custom-styles", "true");
          customStyles.textContent = `
            .jcs__heading {
              font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
              color: #1f2937 !important;
            }
            .jcs__button {
              background-color: #2563eb !important;
              border-radius: 0.375rem !important;
              transition: all 0.2s !important;
            }
            .jcs__button:hover {
              background-color: #1d4ed8 !important;
            }
            .jcs__container {
              border-radius: 0.5rem !important;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
            }
            .jcs__message {
              border-radius: 0.375rem !important;
            }
            .jcs__input {
              border-radius: 0.375rem !important;
              border: 1px solid #d1d5db !important;
            }
            .jcs__input:focus {
              border-color: #3b82f6 !important;
              box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25) !important;
            }
            .scanner__container {
              border-radius: 0.5rem !important;
              overflow: hidden !important;
            }
          `;
          document.head.appendChild(customStyles);
        }

        setScriptsLoaded(true);
      } catch (err: any) {
        setError(err.message);
        console.error("Failed to load bundled scripts:", err);
      }
    };

    if (typeof window !== "undefined") {
      loadScripts();
    }

    return () => {
      // Cleanup scripts and styles
      SCRIPTS.forEach((scriptName) => {
        const script = document.querySelector(
          `script[data-gbg-script="${scriptName}"]`
        );
        if (script) document.head.removeChild(script);
      });

      const link = document.querySelector(
        `link[data-gbg-style="${STYLESHEET}"]`
      );
      if (link) document.head.removeChild(link);

      const customStyles = document.querySelector(
        "style[data-jcs-custom-styles]"
      );
      if (customStyles) document.head.removeChild(customStyles);

      // Cleanup blob URLs
      cleanupBlobUrls();
    };
  }, []); // Remove sdkBasePath dependency since we're using bundled assets

  useEffect(() => {
    const initializeSDK = async () => {
      try {
        if (!window.GBG?.Idscan) {
          throw new Error(
            "GBG.Idscan namespace is not available. Make sure all SDK scripts are loaded correctly."
          );
        }

        if (!containerRef.current) {
          throw new Error("Container element is not available in DOM");
        }

        // Create blob URLs for smart capture assets
        const workerScriptUrl = createBlobUrl(
          "ides-micro.c7f56e19625c4d2863a9.js"
        );
        const asmScriptUrl = createBlobUrl("idesmicro_asm.js");

        const config = {
          backendUrl,
          container: containerRef.current,
          token,
          journeyDefinitionId,
          defaultInputProvider,
          tripleScanAnalyzerGuidancePage,
          fileUploadOnCameraCaptureJourneys,
          previewPages,
          manualCapture,
          assetsDirectory,
          smartCapture: {
            ...smartCapture,
            workerScriptUrl,
            asmScriptUrl,
          },
          isVerboseLogEnabled,
          hideLogo,
          hideAutoCaptureHints,
          ...rest,
        };

        console.log(
          "Initializing GBG ID Scan with container:",
          containerRef.current
        );
        await new window.GBG.Idscan.JourneyContainer(config).initialize();
      } catch (err: any) {
        setError(err.message);
        console.error("Journey initialization failed:", err);
      }
    };

    if (scriptsLoaded && containerRef.current) {
      const timer = setTimeout(() => {
        initializeSDK();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [
    scriptsLoaded,
    backendUrl,
    token,
    journeyDefinitionId,
    defaultInputProvider,
    tripleScanAnalyzerGuidancePage,
    fileUploadOnCameraCaptureJourneys,
    previewPages,
    manualCapture,
    assetsDirectory,
    smartCapture,
    isVerboseLogEnabled,
    hideLogo,
    hideAutoCaptureHints,
  ]);

  if (error) {
    return (
      <div className={`bg-white rounded-lg shadow ${className}`} style={style}>
        <div className="p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <svg
              suppressHydrationWarning
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-red-800 mb-2">
            Initialization Error
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`verification-container ${className}`}
      style={{
        maxWidth: "640px",
        margin: "auto",
        display: "flex",
        flexDirection: "column",
        minHeight: "500px",
        position: "relative",
        ...style,
      }}
      id="gbg-verification-container"
    />
  );
};

declare global {
  interface Window {
    GBG?: {
      Idscan: {
        JourneyContainer: new (config: any) => {
          initialize: () => Promise<void>;
        };
      };
    };
  }
}

export default LocalIdScanJourney;
